import { ChevronRight } from "lucide-react";

const StepCard = ({ number, title, description, details, buyers, sellers }) => {
  return (
    <div 
      className="process-card animate-hidden animate-fadeInUp"
      data-testid={`process-step-${number}`}
    >
      <div className="step-number">{number}</div>
      <h3 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-4 font-serif">
        {title}
      </h3>
      <p className="text-[#475569] mb-4 leading-relaxed">
        {description}
      </p>
      
      {buyers && (
        <div className="mb-4">
          <p className="text-sm font-semibold text-[#0F172A] mb-2">For buyers:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-sm text-[#475569]">
              <ChevronRight className="text-[#D4AF37] mt-0.5 flex-shrink-0" size={14} />
              {buyers[0]}
            </li>
            <li className="flex items-start gap-2 text-sm text-[#475569]">
              <ChevronRight className="text-[#D4AF37] mt-0.5 flex-shrink-0" size={14} />
              {buyers[1]}
            </li>
            <li className="flex items-start gap-2 text-sm text-[#475569]">
              <ChevronRight className="text-[#D4AF37] mt-0.5 flex-shrink-0" size={14} />
              {buyers[2]}
            </li>
          </ul>
        </div>
      )}
      
      {sellers && (
        <div className="mb-4">
          <p className="text-sm font-semibold text-[#0F172A] mb-2">For sellers:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-sm text-[#475569]">
              <ChevronRight className="text-[#D4AF37] mt-0.5 flex-shrink-0" size={14} />
              {sellers[0]}
            </li>
            <li className="flex items-start gap-2 text-sm text-[#475569]">
              <ChevronRight className="text-[#D4AF37] mt-0.5 flex-shrink-0" size={14} />
              {sellers[1]}
            </li>
            <li className="flex items-start gap-2 text-sm text-[#475569]">
              <ChevronRight className="text-[#D4AF37] mt-0.5 flex-shrink-0" size={14} />
              {sellers[2]}
            </li>
          </ul>
        </div>
      )}
      
      <p className="text-[#475569] text-sm leading-relaxed">
        {details}
      </p>
    </div>
  );
};

export const ProcessSection = () => {
  return (
    <section id="process" className="section bg-white" data-testid="process-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20 animate-hidden animate-fadeInUp">
          <div className="gold-line mx-auto" />
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-6 font-serif">
            Our Process
          </h2>
          <p className="text-xl text-[#D4AF37] font-medium mb-4 font-serif">
            A Clear, Thoughtful Process — Designed Around You
          </p>
          <p className="text-lg text-[#475569] max-w-3xl mx-auto leading-relaxed">
            This process was created for people planning a move — whether you're buying, selling, or doing both. Some clients are ready to move now. Others are still gathering information and thinking ahead. The goal is the same: to provide clarity first, then support you through the buying or selling process when the time is right.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-20">
          <StepCard 
            number="1"
            title="Learn Your Options"
            description="We start by understanding your goals, questions, and timeline — whether you're exploring a purchase, preparing to sell, or considering both."
            details="You'll receive clear information about market conditions, pricing considerations, financing basics (if buying), and timing options, so you can make informed decisions without pressure."
          />
          
          <StepCard 
            number="2"
            title="Create a Flexible Plan"
            description="Next, we outline a plan that fits your life."
            details="Plans remain flexible and adjust as your needs evolve."
            buyers={[
              "Identifying preferred locations and home features",
              "Understanding affordability and next steps",
              "Preparing for a smooth purchase timeline"
            ]}
            sellers={[
              "Evaluating preparation and pricing strategies",
              "Exploring timing options",
              "Planning for downsizing or relocation"
            ]}
          />
          
          <StepCard 
            number="3"
            title="Move When the Time Is Right"
            description="When you're ready to move forward, we make the transition smoothly into the buying or selling process."
            details="Every detail is handled with care, clear communication, and steady support — from offer to closing — so you can move with confidence."
          />
        </div>

        <div className="text-center bg-[#F8FAFC] p-10 md:p-16">
          <p className="text-2xl md:text-3xl font-serif text-[#0F172A] italic mb-6">
            "There's no rushing. Only readiness."
          </p>
          <p className="text-lg text-[#475569] max-w-2xl mx-auto">
            Whether you are buying, selling, or planning both, the process is designed to meet you where you are — and move forward only when it feels right.
          </p>
          <p className="text-xl text-[#D4AF37] font-medium mt-6 font-serif">
            Your next move should feel informed, comfortable, and well-supported.
          </p>
        </div>
      </div>
    </section>
  );
};
